import { useState } from 'react'
import { BookMarked, FileText, Loader2, Clock } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useR2List, useR2Read } from '@/hooks/useApi'

export function PlaybooksPanel() {
  const { data, isLoading } = useR2List('playbooks/', 200)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const { data: detail, isLoading: detailLoading } = useR2Read(selectedKey)

  const entries = data?.objects || []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookMarked className="h-4 w-4" />
            Playbooks
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            {!isLoading && <Badge variant="muted" className="text-[10px]">{entries.length} playbooks</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0 && !isLoading ? (
            <div className="text-center py-8 text-sm text-text-muted">
              <BookMarked className="h-8 w-8 mx-auto mb-2 text-text-muted/50" />
              No playbooks yet. Promote patterns via promote_to_playbook.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {entries.map(entry => {
                const filename = entry.key.split('/').pop() || entry.key
                const isSelected = selectedKey === entry.key
                return (
                  <button
                    key={entry.key}
                    onClick={() => setSelectedKey(isSelected ? null : entry.key)}
                    className={`flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${isSelected ? 'bg-accent/10 border border-accent/30' : 'glass-card hover:border-accent/30'}`}
                  >
                    <BookMarked className="h-4 w-4 text-accent shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">{filename}</div>
                      <div className="flex items-center gap-1 text-[10px] text-text-muted mt-0.5">
                        <Clock className="h-3 w-3" />
                        {new Date(entry.uploaded).toLocaleDateString()}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedKey && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4" />
              <span className="truncate font-mono text-xs">{selectedKey}</span>
              {detailLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {detail ? (
              <pre className="text-xs text-text-secondary bg-background rounded-lg p-4 overflow-auto max-h-96 whitespace-pre-wrap font-mono">
                {typeof detail.data === 'string' ? detail.data : JSON.stringify(detail.data, null, 2)}
              </pre>
            ) : detailLoading ? (
              <div className="py-4 text-center text-sm text-text-muted">Loading...</div>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
